/**
 * useSessionRole — lightweight, app-wide hook that classifies the currently
 * signed-in Supabase session as "customer", "staff", or "none" so that the
 * storefront and admin shells can enforce strict role-based session
 * isolation.
 *
 * Why not just useAuth?
 *   `useAuth` already knows whether a profile (staff) row exists, but it does
 *   not know about customers, and many parts of the storefront only have
 *   access to `user`. This hook normalises the answer in a single async
 *   lookup that we cache for the lifetime of the session.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export type SessionContext = "customer" | "staff" | "owner" | "none" | "unknown";

interface ClassifyResult {
  context: SessionContext;
  /** Raw role string when context === "staff" or "owner". */
  role: "super_admin" | "admin" | "manager" | "staff" | null;
}

const cache = new Map<string, ClassifyResult>();

async function classifyUser(user: User): Promise<ClassifyResult> {
  const cached = cache.get(user.id);
  if (cached) return cached;

  // Owner is encoded in user_metadata.is_owner (set by the owner-login edge fn).
  if ((user.user_metadata as any)?.is_owner) {
    const r: ClassifyResult = { context: "owner", role: "super_admin" };
    cache.set(user.id, r);
    return r;
  }

  // Run both lookups in parallel; staff role wins over a stray customer row.
  const [{ data: roleRow }, { data: custRow }] = await Promise.all([
    supabase.from("user_roles").select("role").eq("user_id", user.id).maybeSingle(),
    supabase.from("customers").select("user_id").eq("user_id", user.id).maybeSingle(),
  ]);

  if (roleRow?.role) {
    const r: ClassifyResult = { context: "staff", role: roleRow.role as any };
    cache.set(user.id, r);
    return r;
  }
  if (custRow) {
    const r: ClassifyResult = { context: "customer", role: null };
    cache.set(user.id, r);
    return r;
  }
  // Signed in but classified as neither — treat as none so callers force
  // a sign-out. We DO NOT cache this so a freshly approved staff doesn't
  // get stuck.
  return { context: "none", role: null };
}

export function clearSessionRoleCache(userId?: string) {
  if (userId) cache.delete(userId);
  else cache.clear();
}

export function useSessionRole(): { context: SessionContext; role: ClassifyResult["role"]; loading: boolean } {
  const [state, setState] = useState<{ context: SessionContext; role: ClassifyResult["role"]; loading: boolean }>({
    context: "unknown",
    role: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const apply = async (user: User | null) => {
      if (!user) {
        if (!cancelled) setState({ context: "none", role: null, loading: false });
        return;
      }
      const r = await classifyUser(user);
      if (!cancelled) setState({ ...r, loading: false });
    };

    supabase.auth.getSession().then(({ data }) => apply(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, sess) => {
      if (_evt === "SIGNED_OUT") clearSessionRoleCache();
      apply(sess?.user ?? null);
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  return state;
}
